// Dominio del modo "Mostrar cómo funciona" de **Belleza y Cuidado Personal** — video
// corto (5-8 s) del producto —con una modelo usándolo o solo en escena—, sólo para
// marcas de ese rubro
// (ADR-2026-09-10-modo-mostrar-como-funciona-belleza).
//
// Es el hermano del modo homónimo de Hogar y Decoración
// (`mostrarComoFunciona.ts`, rama `nuevos-modos-hogar-y-deco`), con SU lista de
// decisiones: allá el sujeto es un mueble y las acciones son tres —alguien lo usa, la
// cámara recorre, se transforma—; acá el sujeto es un producto de belleza y lo que se
// elige es si en cuadro hay ALGUIEN o no: una modelo usándolo, o el producto solo.
//
// Vive aparte de la pantalla (`modes/MostrarComoFuncionaBellezaMode.tsx`) por lo mismo
// que `vistasMuebles.ts` vive aparte de su picker: son las listas que decide producto
// —qué acciones existen, qué ambientes se ofrecen, cuánto dura y cuánto cuesta— y
// cambiarlas no debería obligar a abrir el componente.

/**
 * Qué pasa en el video. DOS opciones, y lo que las separa es si hay ALGUIEN en cuadro:
 * `aplica` es una modelo usándolo (sin hablar) y `presenta` es el producto SOLO, sin
 * ninguna persona.
 *
 * Antes las dos tenían modelo y lo que cambiaba era dónde estaba el foco —el envase o el
 * gesto—. Se separaron porque "Mostrando el producto" se leía como "una modelo lo
 * muestra", que es casi lo que promete la otra opción: dos tiles para la misma escena.
 * Ver ADR-2026-09-10-que-pasa-en-el-video-una-modelo-o-el-producto-solo.
 *
 * No existe acá el equivalente de "Se transforma" de Hogar y Deco: un mueble tiene dos
 * estados (cerrado/abierto) y un labial no, así que ese camino —el único que pedía una
 * segunda foto y un endpoint de dos extremos que todavía no existe— no se replicó.
 */
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

export type AccionVideoId = 'presenta' | 'aplica';

export interface AccionVideo {
  id: AccionVideoId;
  nombre: string;
  /** La bajada que aparece debajo de las opciones cuando ésta está elegida. */
  descripcion: string;
  /** El trozo de prompt que aporta esta decisión — ver `promptVideoBelleza`. */
  prompt: string;
  /**
   * El video de EJEMPLO que la muestra pone a la derecha mientras esta acción está
   * elegida. Es lo que hace que "Una modelo usándolo" y "Solo el producto" signifiquen
   * algo concreto antes de gastar créditos: se ve la diferencia, no se lee.
   *
   * Vive acá y no en la pantalla por lo mismo que el resto de la lista: con qué se
   * explica una opción es parte de la opción.
   */
  videoUrl: string;
  /**
   * El frame que se ve mientras el video baja. OPCIONAL: sin él el `<video>` arranca en
   * blanco un instante y listo — no vale la pena bloquear un ejemplo por su póster.
   */
  posterUrl?: string;
}

// El ORDEN es el de la grilla, y `aplica` va PRIMERO porque es el default del modo: la
// promesa de "Mostrar cómo funciona" es ver el producto EN USO, y el producto solo es la
// variante para cuando la marca no quiere a nadie en cuadro.
export const ACCIONES_VIDEO: AccionVideo[] = [
  {
    id: 'aplica',
    get nombre() {
      return `Un ${palabraDeModeloActual().singular} usándolo`;
    },
    descripcion:
      `Un ${palabraDeModeloActual().singular} se lo aplica: el gesto real de uso, en primer plano. No habla ni mira a cámara.`,
    prompt:
      'Una modelo usa el producto sobre su piel o su cabello con el gesto real de aplicación, ' +
      'natural y sin exagerar. El plano es cercano y sigue el movimiento de la mano; el ' +
      'producto entra y sale del encuadre durante el gesto. La textura y el acabado sobre la ' +
      'piel son lo que tiene que verse. La modelo NO habla ni se dirige a la cámara: es el ' +
      'gesto de uso, no un testimonial. Piel realista, sin retoque plástico.',
    // El video del carrusel de bienvenida del rubro: una modelo aplicándose un labial,
    // que es exactamente esta acción.
    videoUrl: '/images/onboarding/rubros/belleza/lapiz/video.mp4',
    posterUrl: '/images/onboarding/rubros/belleza/lapiz/video-poster.jpg',
  },
  {
    id: 'presenta',
    nombre: 'Solo el producto',
    descripcion:
      'El producto solo en escena, sin nadie en cuadro: la cámara lo recorre y se lee la etiqueta.',
    // "Sin nadie en cuadro" se dice tres veces y en NEGATIVO (ni modelo, ni manos, ni
    // cuerpo) a propósito: el resto del prompt describe un comercial de belleza, que es
    // justo el género en el que el modelo de video mete una persona por su cuenta si no
    // se lo prohíben.
    prompt:
      'El producto aparece SOLO en escena, sin ninguna persona en cuadro: no hay modelo, ni ' +
      'manos, ni brazos, ni ninguna parte de un cuerpo. La cámara lo recorre con un ' +
      'movimiento suave —una órbita corta o un acercamiento lento— y el producto se ve ' +
      'entero, nítido y con la etiqueta legible en todo momento. Es un plano de producto de ' +
      'comercial de belleza, sin protagonista humano.',
    videoUrl: '/images/ejemplos_modos/mostrar_como_funciona_belleza/solo-producto.mp4',
  },
];

export const accionVideoPorId = (id: AccionVideoId): AccionVideo =>
  ACCIONES_VIDEO.find((a) => a.id === id) ?? ACCIONES_VIDEO[0];

/**
 * Si en esta acción hay una modelo en cuadro.
 *
 * Vive en el dominio y no en la pantalla porque decide DOS cosas que tienen que moverse
 * juntas: si la columna muestra la card "Modelo", y si la descripción de la modelo entra
 * al prompt. Cuando las dos acciones tenían modelo esto era un `accion === 'aplica'`
 * escrito a mano en la pantalla; ahora que separa dos escenas distintas, el que sabe es
 * este archivo.
 */
export const accionTieneModelo = (id: AccionVideoId): boolean => id === 'aplica';

/**
 * Dónde pasa. Lista FIJA y no la biblioteca de fondos, por el mismo motivo que en el
 * modo de Hogar y Deco: lo que se pide acá es un TIPO DE AMBIENTE con su luz, y la
 * biblioteca mezcla fondos de moda pensados para una foto de producto quieto.
 *
 * Cada ambiente tiene su FOTO (`/images/ambientes_belleza/<id>.png`, ver el README de
 * esa carpeta): el nombre del archivo es el `id`, y la grilla y la card las muestran
 * solas. El ícono de `ICONO_AMBIENTE` queda como respaldo para el que no tenga foto.
 */
export type AmbienteId = 'bano' | 'tocador' | 'spa' | 'estudio' | 'exterior';

export interface Ambiente {
  id: AmbienteId;
  nombre: string;
  /** Lo que se ve alrededor del producto — la luz y los objetos que arman la escena. */
  descripcion: string;
  /** El trozo de prompt que aporta el ambiente. */
  prompt: string;
  imageUrl?: string;
}

export const AMBIENTES: Ambiente[] = [
  {
    id: 'bano',
    nombre: 'Baño luminoso',
    descripcion: 'Mármol claro, espejo y luz de mañana.',
    prompt:
      'un baño luminoso de mármol claro, con espejo y grifería sencilla, luz natural de ' +
      'mañana entrando de costado',
    imageUrl: '/images/ambientes_belleza/bano.png',
  },
  {
    id: 'tocador',
    nombre: 'Tocador',
    descripcion: 'Mesa de maquillaje, luz cálida y espejo con focos.',
    prompt:
      'un tocador de maquillaje con espejo de focos, luz cálida y envolvente, y algunos ' +
      'objetos de rutina apoyados fuera de foco',
    imageUrl: '/images/ambientes_belleza/tocador.png',
  },
  {
    id: 'spa',
    nombre: 'Spa',
    descripcion: 'Toallas, piedras y vapor; luz suave y difusa.',
    prompt:
      'un spa sereno con toallas dobladas, piedras y una leve bruma, luz suave y difusa en ' +
      'tonos neutros',
    imageUrl: '/images/ambientes_belleza/spa.png',
  },
  {
    id: 'estudio',
    nombre: 'Estudio',
    descripcion: 'Fondo liso y luz de estudio, sin distracciones.',
    prompt:
      'un estudio fotográfico con fondo liso de tono neutro y luz de estudio controlada, sin ' +
      'ningún elemento que distraiga',
    imageUrl: '/images/ambientes_belleza/estudio.png',
  },
  {
    id: 'exterior',
    nombre: 'Exterior natural',
    descripcion: 'Luz de día, verde de fondo y aire libre.',
    prompt:
      'un exterior al aire libre con vegetación desenfocada al fondo y luz de día cálida, ' +
      'con brillos naturales sobre la piel',
    imageUrl: '/images/ambientes_belleza/exterior.png',
  },
];

export const ambientePorId = (id: AmbienteId): Ambiente =>
  AMBIENTES.find((a) => a.id === id) ?? AMBIENTES[0];

/**
 * La opción extra de "Dónde pasa": el ambiente lo ESCRIBE el usuario — misma decisión
 * que tomó el modo de Hogar y Deco
 * (ADR-2026-09-04-donde-pasa-tiene-una-opcion-para-describirlo).
 *
 * Vive aparte de `AMBIENTES` a propósito: los curados son escenas con su luz decidida,
 * y éste no es una escena sino un permiso. Metido en la lista, cualquier recorrido de
 * ambientes (la grilla, un futuro `imageUrl`, el prompt) tendría que saltearlo.
 */
export const AMBIENTE_PROPIO_ID = 'propio' as const;

export const AMBIENTE_PROPIO = {
  id: AMBIENTE_PROPIO_ID,
  nombre: 'Lo describo yo',
  descripcion: 'Escribí vos dónde pasa y con qué luz.',
} as const;

/** Lo que puede quedar elegido en "Dónde pasa": uno de los curados, o el propio. */
export type AmbienteElegido = AmbienteId | typeof AMBIENTE_PROPIO_ID;

/** Duración del video, en segundos. Video CORTO: el gesto de uso no da para más. */
export const DURACION_MIN = 5;
export const DURACION_MAX = 8;
export const DURACION_DEFAULT = 6;

/**
 * Créditos por segundo. El modo corre sobre el pipeline de "Animar imagen"
 * (`/v1/videos/from-image`), que cobra `duracion` créditos por video de una escena: la
 * cifra tiene que ser LA MISMA o la pastilla del botón miente
 * (ADR-2026-09-10-mostrar-como-funciona-belleza-corre-sobre-animar-imagen).
 */
export const CREDITOS_POR_SEGUNDO = 1;

export const costoVideo = (duracionSeg: number): number =>
  duracionSeg * CREDITOS_POR_SEGUNDO;

/**
 * Los rasgos de una modelo PERSONALIZADA, en una frase para el prompt.
 *
 * Es el único camino por el que la elección de modelo cambia el video HOY: el endpoint
 * no recibe fotos de referencia, así que una modelo guardada (que es una foto) no cruza,
 * pero una descrita con "Creá tu modelo" —género, edad, tono de piel, pelo— es texto y sí
 * (ADR-2026-09-10-el-picker-de-modelo-es-el-mismo-que-el-del-modo-modelo).
 *
 * Los campos son los de `CustomModelFields`; se pasan sueltos para no atar el dominio a
 * un tipo de la UI. Los vacíos se saltean, y sin ninguno devuelve `null`.
 */
export function descripcionModeloEnPalabras(campos: {
  genero?: string;
  edad?: string;
  tipoCuerpo?: string;
  tonoPiel?: string;
  colorPelo?: string;
  largoPelo?: string;
  descripcion?: string;
}): string | null {
  // El pelo se arma aparte: "castaño" y "largo" sueltos en la lista se leen como dos
  // rasgos inconexos, y juntos son uno solo ("pelo castaño largo").
  const pelo = [campos.colorPelo, campos.largoPelo].filter(Boolean).join(' ');
  const partes = [
    campos.genero,
    campos.edad,
    campos.tipoCuerpo,
    campos.tonoPiel ? `piel ${campos.tonoPiel.toLowerCase()}` : null,
    pelo ? `pelo ${pelo.toLowerCase()}` : null,
    campos.descripcion,
  ]
    .map((p) => p?.trim())
    .filter((p): p is string => !!p);
  return partes.length ? partes.join(', ') : null;
}

/**
 * El prompt que viaja al backend, armado con las decisiones de la columna.
 *
 * Está acá y no en la pantalla porque ES la traducción del dominio: qué significa cada
 * opción para el modelo de video. Si mañana se afina una acción o se suma un ambiente,
 * se toca este archivo y nada más.
 *
 * El orden importa: primero QUÉ pasa (la acción, que es la decisión del modo), después
 * DÓNDE, después las reglas fijas del rubro, y al final lo que escribió el usuario —lo
 * último pesa más y tiene que poder corregir lo anterior—.
 */
export function promptVideoBelleza(opts: {
  accion: AccionVideoId;
  /** El ambiente elegido, o el texto propio cuando es `AMBIENTE_PROPIO_ID`. */
  ambiente: AmbienteElegido;
  ambientePropio: string;
  instruccion: string;
  /**
   * Cómo es la modelo, EN PALABRAS — `modelos.descripcion` de la elegida.
   *
   * La elección de modelo se hace con fotos, y el endpoint de video no recibe fotos de
   * referencia (ADR-2026-09-10-la-modelo-se-elige-con-fotos-y-el-video-solo-lee-texto):
   * lo único que puede cruzar al video es texto. Hoy esa columna está vacía en TODOS los
   * modelos, así que en la práctica no llega nada — pero el día que se carguen, el prompt
   * las usa sin tocar la pantalla.
   */
  modeloDescripcion?: string | null;
}): string {
  const partes: string[] = [accionVideoPorId(opts.accion).prompt];

  const modelo = opts.modeloDescripcion?.trim();
  // Va SEGUNDO, pegado a la acción: es quién hace lo que la acción describe. El guard de
  // la acción es del dominio y no confía en el llamador: en "Solo el producto" no hay
  // nadie en cuadro, y una frase describiendo a la modelo contradiría al prompt que
  // acaba de prohibir que aparezca una.
  if (modelo && accionTieneModelo(opts.accion)) {
    partes.push(`La modelo es ${modelo}.`);
  }

  const dondePasa =
    opts.ambiente === AMBIENTE_PROPIO_ID
      ? opts.ambientePropio.trim()
      : ambientePorId(opts.ambiente).prompt;
  if (dondePasa) partes.push(`La escena transcurre en ${dondePasa}.`);

  // Las reglas del rubro, iguales en las dos acciones: el producto tiene que salir
  // EXACTAMENTE como en la foto de partida —envase, color y etiqueta— porque es el
  // producto real de la marca, y la cámara se mueve poco para que no se deforme.
  //
  // "Piel realista" ya NO está acá sino en el prompt de `aplica`: en "Solo el producto"
  // no hay piel, y nombrarla es justo el tipo de pista con la que el modelo mete una
  // persona en un plano que pidió no tener ninguna.
  partes.push(
    'El producto mantiene exactamente la forma, el color, el envase y la etiqueta de la ' +
      'imagen de partida, sin inventar texto ni cambiar el packaging. Movimiento de cámara ' +
      'suave y contenido, calidad de comercial de belleza.',
  );

  const extra = opts.instruccion.trim();
  if (extra) partes.push(extra);

  return partes.join(' ');
}
